import Map, { Source, Layer, Popup, NavigationControl } from 'react-map-gl/maplibre'
import maplibregl from 'maplibre-gl'
import { useMemo } from 'react'
import { toGeoJSON } from './hotelUtils'
import 'maplibre-gl/dist/maplibre-gl.css'

const MAP_STYLE = 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json'

const clusterLayer = {
  id: 'clusters',
  type: 'circle',
  filter: ['has', 'point_count'],
  paint: {
    'circle-color': '#8b7cf7',
    'circle-radius': ['step', ['get', 'point_count'], 16, 25, 20, 100, 26, 500, 32],
    'circle-opacity': 0.88,
    'circle-stroke-width': 2,
    'circle-stroke-color': '#c4b5fd',
  },
}

const clusterCountLayer = {
  id: 'cluster-count',
  type: 'symbol',
  filter: ['has', 'point_count'],
  layout: {
    'text-field': ['get', 'point_count_abbreviated'],
    'text-size': 12,
  },
  paint: { 'text-color': '#090d14' },
}

const unclusteredLayer = {
  id: 'unclustered-point',
  type: 'circle',
  filter: ['!', ['has', 'point_count']],
  paint: {
    'circle-color': [
      'match',
      ['get', 'creditKind'],
      'both', '#f5c542',
      'edit', '#38bdf8',
      '#4ade80',
    ],
    'circle-radius': 6,
    'circle-stroke-width': 1.5,
    'circle-stroke-color': '#090d14',
  },
}

export default function MapPanel({
  hotels,
  selected,
  onSelect,
  onBoundsChange,
}) {
  const geojson = useMemo(() => toGeoJSON(hotels), [hotels])

  return (
    <Map
      initialViewState={{ longitude: -40, latitude: 28, zoom: 2.2 }}
      mapLib={maplibregl}
      mapStyle={MAP_STYLE}
      style={{ width: '100%', height: '100%' }}
      attributionControl
      onMoveEnd={(e) => {
        const b = e.target.getBounds()
        onBoundsChange({
          west: b.getWest(),
          south: b.getSouth(),
          east: b.getEast(),
          north: b.getNorth(),
        })
      }}
      onLoad={(e) => {
        const b = e.target.getBounds()
        onBoundsChange({
          west: b.getWest(),
          south: b.getSouth(),
          east: b.getEast(),
          north: b.getNorth(),
        })
      }}
      interactiveLayerIds={['clusters', 'unclustered-point']}
      cursor="pointer"
      onClick={(event) => {
        const feature = event.features?.[0]
        if (!feature) {
          onSelect(null)
          return
        }
        if (feature.properties.cluster || feature.properties.cluster === 'true') {
          const map = event.target
          const clusterId = feature.properties.cluster_id
          const source = map.getSource('hotels')
          Promise.resolve(source.getClusterExpansionZoom(clusterId)).then((zoom) => {
            if (zoom == null) return
            map.easeTo({
              center: feature.geometry.coordinates,
              zoom,
            })
          })
          return
        }
        const hotel = hotels.find((h) => h.id === feature.properties.id)
        if (hotel) {
          onSelect(selected?.id === hotel.id ? null : hotel)
        }
      }}
    >
      <NavigationControl position="bottom-right" />
      <Source
        id="hotels"
        type="geojson"
        data={geojson}
        cluster
        clusterMaxZoom={12}
        clusterRadius={42}
      >
        <Layer {...clusterLayer} />
        <Layer {...clusterCountLayer} />
        <Layer {...unclusteredLayer} />
      </Source>
      {selected ? (
        <Popup
          longitude={selected.lng}
          latitude={selected.lat}
          anchor="bottom"
          onClose={() => onSelect(null)}
          closeOnClick
        >
          {selected.name}
        </Popup>
      ) : null}
    </Map>
  )
}
